import { memo } from 'react';

import { Check, HookForm, PageTitle, VerificationCodeField } from '@dec/shared';
import { getErrorMessage } from 'app/components/form/utils';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BooleanParam, StringParam, useQueryParam } from 'use-query-params';

import validations from '../../components/loginWith2fa.validations';
import {
  type LoginWith2faModel,
  type LoginWithRecoveryCodeModel
} from '../../models/loginWith2fa.model';

import { useLoginWith2fa } from './useLoginWith2fa';

interface Props {}

// eslint-disable-next-line react/display-name
export const LoginWith2fa = memo((props: Props) => {
  const [url] = useQueryParam('returnUrl', StringParam);
  const [rememberMe] = useQueryParam('rememberMe', BooleanParam);

  const { loading, isRecoveryMode, setIsRecoveryMode, login, submitRecoveryCode } =
    useLoginWith2fa();

  const { t } = useTranslation();

  const onSubmit = (data: LoginWith2faModel) => {
    data.returnUrl = url as string;
    data.rememberMe = rememberMe as boolean;
    login(data);
  };

  const onSubmitRecovery = (data: LoginWithRecoveryCodeModel) => {
    data.returnUrl = url as string;
    submitRecoveryCode(data);
  };

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<any>();

  return (
    <>
      <div className="container h-100">
        <div className="row align-items-center h-100">
          <div className="col-12 col-md-6 mx-auto">
            <Card className="p-2">
              <Card.Body>
                <Row>
                  <Col className="d-block">
                    <div className="img-fluid mx-auto mb-2 loginLogo"></div>
                  </Col>
                </Row>

                <Row>
                  {isRecoveryMode ? (
                    <Col>
                      <PageTitle title={t('login-with-2fa.title.login-recovery')} />
                    </Col>
                  ) : (
                    <Col>
                      <PageTitle title={t('login-with-2fa.title.login')} />
                    </Col>
                  )}
                </Row>
                {isRecoveryMode ? (
                  <form onSubmit={handleSubmit(onSubmitRecovery)}>
                    <Row>
                      <Col>
                        <Form.Group>
                          <Form.Label>{t('login-with-2fa.label.recoveryCode')}</Form.Label>
                          <input
                            type="text"
                            className="form-control"
                            {...register('recoveryCode', {
                              required: 'The field Recovery code is required'
                            })}
                          ></input>
                          <Form.Control.Feedback type="invalid">
                            {getErrorMessage(errors.recoveryCode)}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Button type="submit" disabled={loading}>
                          {t('login-with-2fa.label.loginButton')}
                        </Button>{' '}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p>{t('login-with-2fa.message.recovery-requested')}</p>
                        <Button
                          className="float-right"
                          variant="secondary"
                          onClick={() => setIsRecoveryMode(false)}
                          disabled={loading}
                        >
                          {t('common.label.back')}
                        </Button>{' '}
                      </Col>
                    </Row>
                  </form>
                ) : (
                  <HookForm validations={validations} onSubmit={onSubmit}>
                    <Row>
                      <Col>
                        <div className="d-flex justify-content-center">
                          <VerificationCodeField
                            name="twoFactorCode"
                            label={t('login-with-2fa.label.verificationCodeInstructions')}
                            digitAriaLabel={(position, total) =>
                              t('enable-authenticator.label.verification-code-digit', {
                                position,
                                total
                              })
                            }
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <Check defaultValue={false} label="" name="rememberMachine" />
                        <Form.Text className="d-block text-muted">
                          {t('login-with-2fa.message.trustedBrowser')}
                        </Form.Text>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Button type="submit" disabled={loading}>
                          {t('login-with-2fa.label.loginButton')}
                        </Button>{' '}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p>{t('login-with-2fa.message.no-access')}</p>
                        <Button
                          className="float-right"
                          variant="secondary"
                          onClick={() => setIsRecoveryMode(true)}
                          disabled={loading}
                        >
                          {t('login-with-2fa.label.loginRecovery')}
                        </Button>{' '}
                      </Col>
                    </Row>
                  </HookForm>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
});
